// hooks/useSnapPoints.js
//
// Singleton snap points cache. Hesaplama tek sefer yapılır, 10+ hook instance'ı
// aynı sonucu paylaşır. getSnapPointsFromScene ağır bir fonksiyon (scene traverse
// + spatial grid), her hook'ta bağımsız çağrılması O(10×N) maliyetliydi.
//
// Mimari:
//   - Module-level _cache objesinden tüm hook instance'ları okur
//   - useEffect dependency'leri değiştiğinde tek bir hesaplama yapılır
//   - _version sayacı ile stale okumaları önler
//   - refreshSnapPoints() dışarıdan tetiklenebilir (move/copy/rotate sonrası)

import { useEffect, useState, useCallback, useRef } from 'react';
import { getSnapPointsFromScene } from './getSnapPointsFromScene';
import { useSelector } from 'react-redux';
import { selectAllWires } from '../../redux/wireSlice';
import { selectAllFloorPlans } from '../../redux/floorPlanSlice';
import { setGlobalSnapTargets, setGlobalSnapMode, setGlobalEnabledSnapTypes } from '../../utils/getActiveFloorPlanId';
import * as THREE from 'three';

// ── Singleton cache ─────────────────────────────────────────────────
let _cache = {
  snapPoints: [],
  version: 0,       // her yeni hesaplamada artar
  scene: null,       // son hesaplanan scene ref
  // Dependency fingerprint — aynı fingerprint'te tekrar hesaplama yapma
  fingerprint: '',
};

// Hesaplama fonksiyonu — module-level, hook dışında da çağrılabilir
function computeSnapPoints(scene, floorPlans, wires, supplyComponents) {
  if (!scene) return [];
  let sceneSnapPoints = getSnapPointsFromScene(scene, floorPlans, wires);

  const simpleSupplySnapPoints = [];
  if (supplyComponents) {
    supplyComponents.forEach(component => {
      if (component.position && (
        component.kind === 'meter_panel' ||
        component.kind === 'dist_panel' ||
        component.kind === 'panel'
      )) {
        simpleSupplySnapPoints.push({
          componentId: component.id,
          position: new THREE.Vector3(component.position.x, component.position.y, component.position.z || 0),
          type: 'simpleSupplyConnection',
          component: component,
          no: component.id
        });
      }
    });
  }

  return [...sceneSnapPoints, ...simpleSupplySnapPoints];
}

// ── Hook ─────────────────────────────────────────────────────────────
const useSnapPoints = (scene) => {
  const [localVersion, setLocalVersion] = useState(_cache.version);
  const floorPlans = useSelector(selectAllFloorPlans);
  const wires = useSelector(selectAllWires);
  const supplyComponentsLength = useSelector((state) => state.simpleSupply.components.length);
  const supplyComponentsRef = useRef(null);
  // Sadece components ref'ini tut — selector'da tüm slice'ı seçme
  supplyComponentsRef.current = useSelector((state) => state.simpleSupply.components);
  const snapTargets = useSelector((state) => state.mods.snapTargets);
  const snapMode = useSelector((state) => state.mods.snapMode);
  const enabledSnapTypes = useSelector((state) => state.mods.enabledSnapTypes);

  // snapTargets/snapMode global sync — sadece ilk mount'ta set
  useEffect(() => { setGlobalSnapTargets(snapTargets); }, [snapTargets]);
  useEffect(() => { setGlobalSnapMode(snapMode); }, [snapMode]);
  useEffect(() => { setGlobalEnabledSnapTypes(enabledSnapTypes); }, [enabledSnapTypes]);

  // Hesaplamayı tetikle — dependency değiştiğinde cache güncellenir.
  // Fingerprint ile 10 instance'ın aynı cycle'da tekrar hesaplamasını önle.
  //
  // ⚡ PERF: scene.children.length yerine YAPISAL (kalıcı) çocuk sayısı kullan.
  // Çizim sırasında snap marker ('__snapMarker'), temp preview hattı ('temp...') ve
  // preview sembolleri (userData.isPreview) mouse hareketiyle sahneye eklenip çıkıyor;
  // ham child sayısı oynayınca ağır getSnapPointsFromScene gereksiz yere tekrar tekrar
  // çalışıyordu → priz/wire çiziminde heap ~3GB'a fırlıyor + korkunç perf. Geçici
  // nesneleri saymayınca sayı çizim boyunca sabit kalır → gereksiz recompute yok.
  let sceneChildrenLen = 0;
  const _kids = scene?.children;
  if (_kids) {
    for (let i = 0; i < _kids.length; i++) {
      const c = _kids[i];
      const nm = c.name;
      if (nm === '__snapMarker') continue;
      if (typeof nm === 'string' && nm.startsWith('temp')) continue;
      if (c.userData && c.userData.isPreview) continue;
      sceneChildrenLen++;
    }
  }
  useEffect(() => {
    if (!scene) return;
    const fp = `${sceneChildrenLen}|${floorPlans.length}|${wires.length}|${supplyComponentsLength}`;
    if (_cache.fingerprint === fp && _cache.scene === scene) {
      // Zaten bu dependency seti ile hesaplanmış — skip
      if (localVersion !== _cache.version) setLocalVersion(_cache.version);
      return;
    }
    _cache.snapPoints = computeSnapPoints(scene, floorPlans, wires, supplyComponentsRef.current);
    _cache.version += 1;
    _cache.scene = scene;
    _cache.fingerprint = fp;
    setLocalVersion(_cache.version);
  }, [sceneChildrenLen, floorPlans.length, wires.length, supplyComponentsLength]);

  // Manuel refresh (move/copy/rotate sonrası)
  const refreshSnapPoints = useCallback(() => {
    if (!scene) return _cache.snapPoints;
    _cache.snapPoints = computeSnapPoints(scene, floorPlans, wires, supplyComponentsRef.current);
    _cache.version += 1;
    _cache.scene = scene;
    _cache.fingerprint = ''; // sonraki auto-effect'in gerekirse yeniden hesaplamasını sağla
    setLocalVersion(_cache.version);
    return _cache.snapPoints;
  }, [scene, floorPlans, wires]);

  // Cache'den oku — localVersion sadece bu instance'ın React'te güncel
  // kalmasını sağlar (re-render tetikler)
  return { snapPoints: _cache.snapPoints, refreshSnapPoints };
};

export default useSnapPoints;
